import React from "react";
import styled from "styled-components";
import GridCardComponent from "./generic/GridCardComponent";
import { defaultStatusColors, getSimpleColorSpan, getSpanWithColor } from "../../../utils/CardUtils";
import moment from "moment/moment";
/* #region  SCOPED CSS */
const StyledPage = styled.div``;
/* #endregion */

/**
 * Renders a grid card component for displaying store information.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {string} props.className - The CSS class name for the component.
 * @param {string} props.title - The title of the store.
 * @param {string} props.id - The ID of the store.
 * @param {boolean} props.hasBorders - Indicates if the card has borders.
 * @param {string} [props.titlePrefix="STORE"] - The prefix for the title.
 * @param {string} [props.idPrefix="BOTAG ID"] - The prefix for the ID.
 * @param {boolean} props.isCardEnabled - Indicates if the card is enabled.
 * @param {boolean} props.isCardSuspended - Indicates if the card is suspended.
 * @param {string} props.image - The image URL for the store.
 * @param {Object[]} props.details - The details to display on the card.
 * @param {string} props.details[].key - The key of the detail.
 * @param {string} props.details[].value - The value of the detail.
 * @param {Object} props.edit - The edit button configuration.
 * @param {Object} props.suspend - The suspend button configuration.
 * @param {Object} props.delete - The delete button configuration.
 * @returns {JSX.Element} The rendered grid card component.
 */
export default function StoresGridCard(props) {
    const status = props?.data?.status || "--";
    const deliveries = [];

    if (props?.data?.pickup) deliveries.push("PK");
    if (props?.data?.courier) deliveries.push("CO");
    if (props?.data?.shipping) deliveries.push("SH");

    let deliveriesText = deliveries.join(", ");
    if (deliveriesText === "") deliveriesText = props?.id ? "None" : "-";

    //computed vars
    const createdBy = props?.data?.createdBy || "--";
    const createdOn = props?.data?.createdOn ? moment(props?.data?.createdOn).format("DD/MM/YYYY") : "--";
    const company = props?.data?.companyName || "--";
    const retailBrand = props?.data?.retailBrandName || "--";
    const POSsystem = props?.data?.POSsystem || "--";
    const POSstatus =
        props?.data?.POSstatus === "--" ? "--" : getSpanWithColor(props?.data?.POSstatus || "--", defaultStatusColors);

    const brandsConnected = props?.data?.brandsConnected ?? "--";
    const ffcentersValue = props?.data?.FFCenters ?? "--";
    const ffcenters = getSimpleColorSpan(
        ffcentersValue,
        ffcentersValue < brandsConnected ? "orange" : ffcentersValue === "No" ? "red" : "blue"
    );

    const sharingStockValue = props?.data?.sharingStock ?? "--";
    const sharingPricesValue = props?.data?.sharingPrices ?? "--";
    const sharingSalesValue = props?.data?.sharingSales ?? "--";
    const ordersPendingValue = props?.data?.ordersPending ?? "--";

    const sharingStock = getSimpleColorSpan(
        sharingStockValue,
        sharingStockValue === "Yes" ? "green" : sharingStockValue === "No" ? "red" : "blue"
    );
    const sharingPrices = getSimpleColorSpan(
        sharingPricesValue,
        sharingPricesValue === "Yes" ? "green" : sharingPricesValue === "No" ? "red" : "blue"
    );
    const sharingSales = getSimpleColorSpan(
        sharingSalesValue,
        sharingSalesValue === "Yes" ? "green" : sharingSalesValue === "No" ? "red" : "blue"
    );
    const ordersPending = getSimpleColorSpan(
        ordersPendingValue,
        ordersPendingValue === "Yes" ? "orange" : ordersPendingValue === "No" ? "green" : "blue"
    );

    return (
        <StyledPage className={`${props?.className || ""}`}>
            <GridCardComponent
                primaryButton={props?.primaryButton ?? { title: "CONNECT POS" }}
                {...(props?.POSstatus !== "Not connected" && {
                    secondaryButton: props?.secondaryButton ?? { title: "Add Staff" }
                })}
                title={props?.title}
                id={props?.id}
                hasBorders={props?.hasBorders}
                titlePrefix={props?.titlePrefix || "STORE"}
                idPrefix={props?.idPrefix || "BOTAG ID"}
                isCardEnabled={props?.isCardEnabled}
                isCardSuspended={props?.isCardSuspended}
                image={props?.image}
                imageFit="cover"
                details={[
                    { key: "Created by:", value: createdBy },
                    { key: "Created on:", value: createdOn },
                    { key: "Company:", value: company },
                    { key: "Retail brand: ", value: retailBrand },
                    { key: "Deliveries:", value: deliveriesText },
                    { key: "Brands connected:", value: brandsConnected },
                    { key: "FFCenters:", value: ffcenters },

                    { key: "POS:", value: POSsystem },
                    { key: "POS status:", value: POSstatus },
                    { key: "Status:", value: getSpanWithColor(status ?? "--", defaultStatusColors) },
                    { key: "Sharing stock:", value: sharingStock },
                    { key: "Sharing prices:", value: sharingPrices },
                    { key: "Sharing sales:", value: sharingSales },
                    { key: "Orders pending:", value: ordersPending }
                ]}
                edit={{ hidden: false, ...props?.edit }}
                suspend={props?.suspend}
                delete={props?.delete}
                noOfRows={7}
            />
        </StyledPage>
    );
}
